﻿import type { ReservationState } from './types';
import type { ProgramBookingState } from './model.Programs';

import { default as classNames } from "classnames";

import { format, differenceInDays } from 'date-fns';
import { useEffect, useState } from 'react';

import Modal from 'react-bootstrap/Modal';
import { getProgramTypeDisplayName } from './OrderDetailsComponent';
import { countCalendarDaysBetween, decimalToTHB, stringIsNullOrEmpty } from '@/utils/utils';
import { getBreakdownSourceLabel } from '@/utils/priceBreakdown';

export function ProgramPriceDialogComponent({ reservationState, programBookingState, visible, closed }: ProgramPriceDialogComponentProps) {

    let baseTotal = 0;
    let payableTotal = 0;

    for (const programDate of programBookingState.Dates) {
        baseTotal += programDate.CalculatedPrice.totalBaseAmount;
        payableTotal += programDate.CalculatedPrice.totalPayableAmount;
    }

    const discounted = payableTotal < baseTotal;

    const nights = countCalendarDaysBetween(programBookingState.Start.value, programBookingState.End.value);

    return (
        <Modal centered backdrop="static" show={visible} onHide={() => { closed?.(); }} size="lg" >
            <Modal.Header closeButton>
                <Modal.Title>Program Price</Modal.Title>
            </Modal.Header>
            <Modal.Body>

                <table className="table">
                    <tbody>
                        <tr>
                            <th colSpan={2}>
                                <div className="mb-2 text-lg">
                                    <div>{getProgramTypeDisplayName(reservationState, programBookingState.ProgramType.value)}</div>
                                    <div>{format(programBookingState.Start.value, 'dd/MM/yyyy')} - {format(programBookingState.End.value, 'dd/MM/yyyy')} ({nights} days)</div>
                                </div>
                            </th>
                        </tr>
                        {programBookingState.Dates.map((programDate, i) => {

                            if (programDate.PriceBreakdown) {

                                return [
                                    (
                                        <tr key={`${programDate.Date.getTime().toString()}_0`}>
                                            <th className={classNames({ 'pt-4': i > 0 }, "font-bold")} colSpan={2}>
                                                {format(programDate.Date, 'dd/MM/yyyy')}
                                            </th>
                                        </tr>
                                    ),
                                    (
                                        <tr key={`${programDate.Date.getTime().toString()}_1`}>
                                            <td>
                                                {getBreakdownSourceLabel(programDate.PriceBreakdown)}
                                            </td>
                                            <td className="text-right">
                                                {decimalToTHB(programDate.PriceBreakdown.BaseAmount.Amount.Gross)}
                                            </td>
                                        </tr>
                                    ),
                                    ...programDate.PriceBreakdown.Multipliers.map((multiplier, j) => {
                                        return [(
                                            <tr key={`${programDate.Date.getTime().toString()}_${j + 2}`}>
                                                <td>
                                                    {multiplier.Source} ({multiplier.Multiplier * 100}%)
                                                </td>
                                                <td className="text-right">
                                                    {decimalToTHB(multiplier.AdjustmentAmount.Gross)}
                                                </td>
                                            </tr>
                                        )];
                                    }),
                                    ...programDate.PriceBreakdown.Discounts.map((discount, j) => {
                                        return [(
                                            <tr key={`${programDate.Date.getTime().toString()}_${j + 2}`}>
                                                <td>
                                                    {discount.Source} ({discount.Multiplier * 100}%)
                                                </td>
                                                <td className="text-right">
                                                    {decimalToTHB(discount.AdjustmentAmount.Gross)}
                                                </td>
                                            </tr>
                                        )];
                                    }),
                                    ...programDate.VoucherEffects.map((voucher, i) => {
                                        return [(
                                            <tr key={`${programDate.Date.getTime().toString()}_${i + 2}`}>
                                                {voucher.Effects.map((e, j) => {
                                                    return [(
                                                        <td key={`${programDate.Date.getTime().toString()}_${i + 2}_Effects_${j}_1`}>
                                                            <div className="max-w-xs">
                                                                {voucher.Name}{(!stringIsNullOrEmpty(voucher.Code) ? ` (${voucher.Code})` : "")} - {e.Effect}
                                                            </div>
                                                        </td>
                                                    ),
                                                    (
                                                        <td className="text-right" key={`${programDate.Date.getTime().toString()}_${i + 2}_Effects_${j}_2`}>
                                                            {decimalToTHB(e.Amount * -1)}
                                                        </td>
                                                    )];
                                                })}
                                            </tr>
                                        )];
                                    }),
                                    ...programDate.MinorAdjustment.value ? [
                                        (
                                            <tr key={`${programDate.Date.getTime().toString()}_minoradjustment`} >
                                                <td>
                                                    Minor Adjustment
                                                </td>
                                                <td className="text-right">
                                                    {decimalToTHB(programDate.MinorAdjustment.value)}
                                                </td>
                                            </tr>
                                        )
                                    ] : [],
                                    <tr className="font-bold" key={`${programDate.Date.getTime().toString()}_${programDate.VoucherEffects.length + 3}`}>
                                        <td>
                                            Total
                                        </td>
                                        <td className="text-right">
                                            {programDate.CalculatedPrice.totalPayableAmount.toLocaleString('en-NZ', { style: 'currency', currency: 'THB' })}
                                        </td>
                                    </tr>
                                ];
                            }

                            return [
                                (
                                    <tr key={`${programDate.Date.getTime().toString()}_0`}>
                                        <th className={classNames({ 'pt-4': i > 0 }, "font-bold")} colSpan={2}>
                                            {format(programDate.Date, 'dd/MM/yyyy')}
                                        </th>
                                    </tr>
                                ),
                                (programDate.CalculatedPrice.totalBaseAmount > programDate.CalculatedPrice.totalPayableAmount) && (
                                    <tr key={`${programDate.Date.getTime().toString()}_1`}>
                                        <td>
                                            Base
                                        </td>
                                        <td className="text-right">
                                            {programDate.CalculatedPrice.totalBaseAmount.toLocaleString('en-NZ', { style: 'currency', currency: 'THB' })}
                                        </td>
                                    </tr>
                                ),
                                ...programDate.VoucherEffects.map((voucher, i) => {
                                    return [(
                                        <tr key={`${programDate.Date.getTime().toString()}_${i + 2}`}>
                                            {voucher.Effects.map((e, j) => {
                                                return [(
                                                    <td key={`${programDate.Date.getTime().toString()}_${i + 2}_Effects_${j}_1`}>
                                                        <div className="max-w-xs">
                                                            {voucher.Name}{(!stringIsNullOrEmpty(voucher.Code) ? ` (${voucher.Code})` : "")} - {e.Effect}
                                                        </div>
                                                    </td>
                                                ),
                                                (
                                                    <td className="text-right" key={`${programDate.Date.getTime().toString()}_${i + 2}_Effects_${j}_2`}>
                                                        {e.Amount.toLocaleString('en-NZ', { style: 'currency', currency: 'THB' })}
                                                    </td>
                                                )];
                                            })}
                                        </tr>
                                    )];
                                }),
                                (
                                    <tr className="font-bold" key={`${programDate.Date.getTime().toString()}_${programDate.VoucherEffects.length + 3}`}>
                                        <td>
                                            Total
                                        </td>
                                        <td className="text-right">
                                            {programDate.CalculatedPrice.totalPayableAmount.toLocaleString('en-NZ', { style: 'currency', currency: 'THB' })}
                                        </td>
                                    </tr>
                                )];
                        })}
                        {discounted && (
                            <tr className="font-bold">
                                <td className="pt-4">
                                    Base Total
                                </td>
                                <td className="pt-4 text-right line-through">
                                    {baseTotal.toLocaleString('en-NZ', { style: 'currency', currency: 'THB' })}
                                </td>
                            </tr>
                        )}
                        <tr className="font-bold">
                            <td className={classNames({ 'pt-4': !discounted })}>
                                {discounted ? "Payable Total" : "Total"}
                            </td>
                            <td className={classNames({ 'pt-4': !discounted }, "text-right")}>
                                {payableTotal.toLocaleString('en-NZ', { style: 'currency', currency: 'THB' })}
                            </td>
                        </tr>
                    </tbody>
                </table>
            </Modal.Body>
        </Modal>
    );
}

export interface ProgramPriceDialogComponentProps {
    reservationState: ReservationState;
    programBookingState: ProgramBookingState;
    visible: boolean;
    closed?: () => void;
}
